import React from 'react';
import { SearchField as Field } from './search/SearchPanel';
import { SEARCH_INPUT_CLASS as inputClass } from './search/searchStyles';
import { TASK_STATUSES } from '../utils/taskStatus';
import { TASK_FILTER_STATUSES, TASK_TYPES } from '../utils/dashboardFilters';

// A labelled on/off switch, in the app's toggle style
function Toggle({ label, checked, onChange, disabled = false, note = null }) {
  return (
    <label className={`inline-flex items-center gap-2 text-sm font-medium ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="relative h-6 w-10 flex-none rounded-full bg-gray-300 transition-all peer-checked:bg-emerald-500 peer-focus:ring-3 peer-focus:ring-emerald-500/50 before:absolute before:top-1 before:left-1 before:size-4 before:rounded-full before:bg-white before:transition-transform before:content-[''] peer-checked:before:translate-x-full dark:bg-gray-700" aria-hidden="true" />
      {label}
      {note && <span className="font-normal text-gray-600 dark:text-gray-300" data-testid="mine-only-note">{note}</span>}
    </label>
  );
}

/**
 * The dashboard's task filters and toggles, shown in its filter panel under the trial filters.
 * Controlled: `filters` (utils/dashboardFilters.js), `onChange(patch)`. `mineOnlyOverriddenBy`: the name of
 * the other user chosen in "Assigned to" - "Mine only" is then off (and greyed out) for as long.
 */
export default function DashboardTaskFilters({ filters, onChange, users = [], mineOnlyOverriddenBy = null }) {
  const toggleStatus = (status) => onChange({
    statuses: filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status],
  });

  return (
    <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700" data-testid="task-filters">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <fieldset className="sm:col-span-2">
          <legend className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-300">Status</legend>
          <div className="flex flex-wrap gap-1.5">
            {TASK_FILTER_STATUSES.map((status) => {
              const on = filters.statuses.includes(status);
              // The same chip for every status: off it is outlined grey; on it takes the status's colours and
              // keeps an outline in its own colour, so a pale status (No date) is still seen as a chosen button
              return (
                <label
                  key={status}
                  data-testid="status-chip"
                  data-on={on}
                  className={`cursor-pointer rounded-full border px-2.5 py-1 text-xs font-semibold ${on ? `${TASK_STATUSES[status].pill} border-current` : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'}`}
                >
                  <input type="checkbox" className="sr-only" checked={on} onChange={() => toggleStatus(status)} />
                  {TASK_STATUSES[status].label}
                </label>
              );
            })}
          </div>
        </fieldset>
        <Field label="Type">
          <select value={filters.type} onChange={(e) => onChange({ type: e.target.value })} className={inputClass}>
            <option value="">All types</option>
            {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Assigned to">
          <select value={filters.assignee} onChange={(e) => onChange({ assignee: e.target.value })} className={inputClass}>
            <option value="">Anyone</option>
            <option value="unassigned">Unassigned</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name || u.initials || u.email}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        <Toggle
          label="Mine only"
          checked={filters.mineOnly && !mineOnlyOverriddenBy}
          disabled={!!mineOnlyOverriddenBy}
          note={mineOnlyOverriddenBy ? `Showing tasks assigned to ${mineOnlyOverriddenBy}` : null}
          onChange={(mineOnly) => onChange({ mineOnly })}
        />
        <Toggle label="Hide overdue older than 6 months" checked={filters.hideOldOverdue} onChange={(hideOldOverdue) => onChange({ hideOldOverdue })} />
      </div>
    </div>
  );
}
